import { useRootStackNavigation } from '$tablet-app/src/app/navigation/hooks';
import { useLoginScreenState } from '$tablet-app/src/app/screens/LoginScreen/login_screen_state';
import { useStateStore } from '$tablet-app/src/app/stores/useStateStore';
import { getFirst, hasItems } from '@akin-home/arrays';
import { useTabletDashboardForUser } from '@akin-home/dashboards/react';
import { findByID } from '@akin-home/records';
import { findManagerUser, findPrimaryUser } from '@akin-home/user';
import { useEffect, useMemo } from 'react';
import { useSetCurrentUser } from '../../hooks';
import { useListCurrentHabitatUsers } from '../../hooks/useCurrentHabitatUsers';
import { useCurrentUser } from '../../hooks/useCurrentUser';

/**
 * Provides the current user. If none is selected, shows the user selection screen.
 * The rest of the app should be wrapped in this.
 */
export const CurrentUserProvider = (props: { children: JSX.Element }) => {
  const { data: users } = useListCurrentHabitatUsers();
  const { navigate } = useRootStackNavigation();

  const cached_user_id = useStateStore((state) => state.current_user_id);

  const { data, isLoading } = useTabletDashboardForUser(cached_user_id);

  const has_dashboard = !!data;

  const closeWelcome = useLoginScreenState((state) => state.closeWelcome);

  const setCurrentUser = useSetCurrentUser();
  const current_user = useCurrentUser();
  const setUsers = useStateStore((state) => state.setUsers);

  const profile = useMemo(() => {
    if (!hasItems(users)) return;

    return (
      findByID(users, cached_user_id) ||
      findManagerUser(users) ||
      findPrimaryUser(users) ||
      getFirst(users)
    );
  }, [users, cached_user_id]);

  useEffect(() => {
    setUsers(users);
  }, [setUsers, users]);

  useEffect(() => {
    if (profile) {
      /** This is here for ensure that users who are already logged in
       * prior to welcome screen update will not be presented with it if they log out */
      closeWelcome();
      setCurrentUser(profile);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [profile, setCurrentUser]);

  useEffect(() => {
    if (!isLoading && !has_dashboard && cached_user_id) {
      /** Give the navigator a chance to init */
      setTimeout(() => {
        navigate('Onboarding');
      }, 100);
    }
  }, [isLoading, has_dashboard, cached_user_id, navigate]);

  return current_user && !isLoading ? props.children : null;
};
